import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { ArrowUpRight } from "lucide-react";
import { DataTable } from "~/components/data-table";
import { FilterTabs, type FilterTab } from "~/components/filter-tabs";
import { MasterDetail } from "~/components/master-detail";
import { ParcelConversation, ResolveConversation } from "~/components/ops/parcel-conversation";
import { UnreadPill, useOpsUnread } from "~/components/ops/unread";
import { PageHeader } from "~/components/page-header";
import { SearchInput } from "~/components/search-input";
import { Button } from "~/components/ui/button";
import { api } from "~/lib/api";
import { formatDate } from "~/lib/format";
import type { IConversation } from "~/lib/proto";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_authed/ops/messages")({
  component: Messages,
});

type Stage = "reply" | "waiting" | "resolved";
type Filter = Stage | "all";

function stageOf(conversation: IConversation): Stage {
  if (conversation.resolved) return "resolved";
  return conversation.lastAuthorRole === "customer" ? "reply" : "waiting";
}

function since(iso?: string | null): string {
  if (!iso) return "";
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? "" : formatDistanceToNowStrict(date);
}

function matches(conversation: IConversation, query: string): boolean {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  return [conversation.customerName, conversation.customerPhone, conversation.description, conversation.trackingNumber, conversation.lastMessage].some(
    (field) => field?.toLowerCase().includes(term),
  );
}

function Messages() {
  const [filter, setFilter] = useState<Filter>("reply");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<IConversation | null>(null);

  const conversations = useQuery({ queryKey: ["ops", "conversations"], queryFn: () => api.ops.conversations() });
  const unread = useOpsUnread();
  const all = conversations.data ?? [];
  const shown = all.filter((c) => (filter === "all" || stageOf(c) === filter) && matches(c, query));
  const count = (stage: Stage) => all.filter((c) => stageOf(c) === stage).length;

  const tabs: FilterTab<Filter>[] = [
    { value: "reply", label: "Needs a reply", count: count("reply") },
    { value: "waiting", label: "Waiting on customer", count: count("waiting") },
    { value: "resolved", label: "Resolved" },
    { value: "all", label: "All" },
  ];

  const columns = useMemo<ColumnDef<IConversation, unknown>[]>(
    () => [
      {
        id: "customer",
        header: "Customer",
        cell: ({ row }) => {
          const c = row.original;
          const waitingMessages = unread.data?.parcel[c.parcelId ?? ""] ?? 0;
          return (
            <div className="flex max-w-[150px] min-w-0 items-center gap-2 @md:max-w-[170px]">
              <div className="min-w-0 leading-tight">
                <p className="truncate text-[13px] font-medium">{c.customerName || "No name"}</p>
                <p className="text-text-faint truncate text-[11px]">{c.description || "Parcel"}</p>
              </div>
              {waitingMessages > 0 && <UnreadPill count={waitingMessages} />}
            </div>
          );
        },
      },
      {
        id: "last",
        header: "Last message",
        cell: ({ row }) => {
          const c = row.original;
          const fromTeam = c.lastAuthorRole !== "customer";
          return (
            <p className={cn("max-w-[130px] truncate text-[13px] @md:max-w-[260px] @3xl:max-w-[420px]", fromTeam && "text-text-faint")}>
              {fromTeam ? "You: " : ""}
              {c.lastMessage}
            </p>
          );
        },
      },
      {
        id: "waiting",
        header: "Waiting",
        cell: ({ row }) => {
          const c = row.original;
          return (
            <span
              className={cn("tabular text-[12px] whitespace-nowrap", stageOf(c) === "reply" ? "font-medium" : "text-text-faint")}
              title={formatDate(c.lastMessageAt)}
            >
              {since(c.lastMessageAt)}
            </span>
          );
        },
        meta: { headClassName: "hidden w-[100px] @md:table-cell", cellClassName: "hidden @md:table-cell" },
      },
    ],
    [unread.data],
  );

  return (
    <>
      <PageHeader
        eyebrow="Customers"
        title="Messages"
        caption={
          conversations.isLoading ? undefined : count("reply") === 0 ? "Nobody is waiting for a reply" : `${count("reply")} waiting for a reply`
        }
      />

      <FilterTabs
        label="Filter conversations"
        value={filter}
        tabs={tabs}
        onChange={(next) => {
          setFilter(next);
          setSelected(null);
        }}
      />

      <SearchInput className="max-w-md" value={query} onChange={setQuery} placeholder="Name, phone, tracking number or message" />

      {conversations.isError ? (
        <div className="border-hairline bg-card rounded-lg border px-6 py-8 text-center">
          <p className="text-[14px] font-medium">Messages did not load</p>
          <p className="text-text-faint mt-1 text-[13px]">This is a connection problem, not an empty inbox.</p>
          <Button variant="outline" className="mt-3" onClick={() => void conversations.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <MasterDetail
          open={!!selected}
          onClose={() => setSelected(null)}
          detail={selected ? <Thread key={selected.parcelId} conversation={selected} /> : null}
          emptyHint="Select a conversation to reply"
          title="Conversation"
        >
          <DataTable
            data={shown}
            columns={columns}
            limit={shown.length || 50}
            offset={0}
            onPageChange={() => {}}
            loading={conversations.isLoading}
            getRowId={(c) => c.parcelId!}
            onRowClick={setSelected}
            selectedRowId={selected?.parcelId ?? undefined}
            caption="Customer conversations, newest first"
            emptyTitle={query.trim() ? "No matches" : filter === "reply" ? "All caught up" : "Nothing here"}
            emptyBody={
              query.trim()
                ? "Try the customer's name, phone number or tracking number."
                : filter === "reply"
                  ? "No customer is waiting for a reply."
                  : "Conversations appear here as customers and staff write."
            }
          />
        </MasterDetail>
      )}
    </>
  );
}

function Thread({ conversation }: { conversation: IConversation }) {
  const parcelId = conversation.parcelId!;
  return (
    <div className="flex h-[calc(100svh-6rem)] flex-col max-md:h-full">
      <header className="border-hairline border-b p-4 md:pr-10">
        <h2 className="section-heading truncate">{conversation.customerName || "No name"}</h2>
        <p className="text-text-faint mt-0.5 flex flex-wrap gap-x-3 text-[12px]">
          {conversation.customerPhone && (
            <a href={`tel:${conversation.customerPhone}`} className="text-foreground tabular underline-offset-2 hover:underline">
              {conversation.customerPhone}
            </a>
          )}
          <span className="min-w-0 truncate">
            {[conversation.description, conversation.trackingNumber].filter(Boolean).join(", ")}
          </span>
        </p>
        <div className="mt-3 flex items-start justify-between gap-3">
          <Button asChild size="sm" variant="outline" className="max-md:h-10">
            <Link to="/ops/parcels" search={{ parcel: parcelId }}>
              Open parcel <ArrowUpRight />
            </Link>
          </Button>
          <ResolveConversation parcelId={parcelId} />
        </div>
      </header>
      <ParcelConversation parcelId={parcelId} fill />
    </div>
  );
}
